import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Fade, IconButton, Tooltip } from '@material-ui/core';
import { PaletteRounded as PaletteRoundedIcon, CloseRounded as CloseRoundedIcon } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import { PopupV1 as Popup, POPUP_TYPE } from '../popup';
import PPButton from '../button';
import { CommonModalStyleV3 as CommonModalStyle } from "../../../styles/commonModalStyle";

const THEME_PALETTE_DEFAULT_VERSION = Number(process.env.REACT_APP_COMMON_MODAL_THEME_PALETTE_DEFAULT_VERSION);

const hasThemePalette = process.env.REACT_APP_ENABLE_COMMON_MODAL_THEME_PALETTE === 'true';

const root = document.getElementById('portal');

const mobile = /Mobi|Android/i.test((window && window.navigator && window.navigator.userAgent) || '');

export const modalWidth = {
  xs: 540,
  s: 620,
  s2: 760,
  m: 960,
  l: 1024,
  xl: 1300
};

export const modalHeight = {
  xs: 540,
  s: 620,
  s2: 760,
  m: 960,
  l: 1024,
  xl: 1300
};

export const modalTop = {
  s: '20vh',
  m: '6vh',
};

export const modalLeft = {
  s: '30vw',
  m: '26vw',
};

const initialPoistion = {
  numeric: {
    top: 8,
    left: 8
  },
  px: {
    top: '8px',
    left: '8px'
  }
};

const animationDuration = '0.2s';

const FadeInAndOutDuration = 250; // duration is milliseconds

export { POPUP_TYPE };

const Event = {
  mouseup: 'mouseup',
  mousedown: 'mousedown',
  mousemove: 'mousemove',

  touchstart: 'touchstart',
  touchend: 'touchend',
  touchmove: 'touchmove'
};

const ModalThemes = new Map([
  [0, {
    title: 'A',
    wrapper: 'bg-dark-primary-v3 p-6',
    heading: 'color-black bg-white-v2',
    paletteIcon: 'color-black-important',
  }],
  [1, {
    title: 'B',
    wrapper: 'bg-dark-primary-v3 p-6',
    heading: 'color-white bg-dark-primary-v2',
    paletteIcon: 'color-white-important',
  }],
  [2, {
    title: 'C',
    wrapper: 'bg-dark-primary-v2 p-6',
    heading: 'color-white bg-dark-primary-v2',
    paletteIcon: 'color-white-important',
  }],
  [3, {
    title: 'D',
    wrapper: 'bg-dark-primary-v2 border-2-bg-dark-primary-v2',
    heading: 'color-white bg-dark-primary-v2',
    paletteIcon: 'color-white-important',
  }],
  [4, {
    title: 'E',
    wrapper: 'bg-dark-primary-v3 p-6',
    heading: 'color-black bg-lighter-grey',
    paletteIcon: 'color-black-important',
  }],
]);

const Component = ({
  title: heading,
  children: content,
  open,

  // Confirmation popup on save
  saveConfirmation = false,
  saveConfirmationHeading = 'Confirmation',
  saveConfirmationMessage = 'Are you sure you want to save?',
  onSaveConfirmationYes: handleSaveConfirmationYes = () => {},
  onSaveConfirmationNo: handleSaveConfirmationNo = () => {},

  // Confirmation popup on close
  closeConfirmation = false,
  closeConfirmationHeading = 'Confirmation',
  closeConfirmationMessage = 'Are you sure you want to cancel?',
  onCloseConfirmationYes: handleCloseConfirmationYes = () => {},
  onCloseConfirmationNo: handleCloseConfirmationNo = () => {},

  // Alert popup on response
  responsePopup = false,
  responsePopupType = POPUP_TYPE.INFO,
  responsePopupHeading = '',
  responsePopupMessage = '',
  onResponsePopupOk: handleResponsePopupOk = () => {},

  classes,
  style = {
    width: modalWidth.s
  },
  
  themeVersion = THEME_PALETTE_DEFAULT_VERSION,
  hasClose = false,
  onClose: handleClose = () => {},
}) => {
  const [_themeVersion, setThemeVersion] = useState(themeVersion);
  const theme = useMemo(() => ModalThemes.get(_themeVersion), [_themeVersion]);
  const ref = {
    modal: useRef(),
    heading: useRef(),
    utilityPanel: useRef(),
  };
  const offsetX = useRef(0);
  const offsetY = useRef(0);

  const handleAdjustPosition = useCallback(() => {
    const maxBottom = window.innerHeight - initialPoistion.numeric.top;
    const maxRight = window.innerWidth - initialPoistion.numeric.left;
    const { left, right, top, bottom } = ref.modal.current.getBoundingClientRect();
    const position = { top: `${top}px`, left: `${left}px` };

    if (top < initialPoistion.numeric.top) {
      position.top = initialPoistion.px.top;
    }
    if (left < initialPoistion.numeric.left) {
      position.left = initialPoistion.px.left;
    }
    if (bottom > maxBottom) {
      position.top = `${top - (bottom - maxBottom)}px`;
    }
    if (right > maxRight) {
      position.left = `${left - (right - maxRight)}px`;
    }

    handleSetPosition(position.top, position.left);
  }, []);

  const handleDragging = {
    start: useCallback((event) => {
      if (mobile && event.type === Event.mousedown) {
        return;
      }
  
      const movement = mobile ? event.touches[0] : event;
      const { clientX, clientY } = movement;
      const { offsetLeft, offsetTop } = ref.modal.current;
      offsetX.current = clientX - offsetLeft;
      offsetY.current = clientY - offsetTop;
      ref.heading.current.classList.add('grabbing');
      document.addEventListener(mobile ? Event.touchmove : Event.mousemove, handleDragging.move);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
    end: useCallback((event) => {
      ref.heading.current.classList.remove('grabbing');
      document.removeEventListener(mobile ? Event.touchmove : Event.mousemove, handleDragging.move);
      handleAdjustPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
    move: useCallback((event) => {
      event.preventDefault();
      const movement = mobile ? event.touches[0] : event;
      const { clientX, clientY } = movement;
      const left = `${clientX - offsetX.current}px`;
      const top = `${clientY - offsetY.current}px`;
      ref.modal.current.style.transition = 'none';
      ref.modal.current.style.left = left;
      ref.modal.current.style.top = top;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  };

  const handleSetPosition = useCallback((top = initialPoistion.px.top, left = initialPoistion.px.left) => {
    if (ref.modal.current) {
      ref.modal.current.style.transition = animationDuration;
      ref.modal.current.style.top = top;
      ref.modal.current.style.left = left;
    }
    setTimeout(() => {
      if(ref.modal.current) ref.modal.current.style.transition = 'none';
    }, 300);
  }, []);

  const handlePaletteClick = useCallback(() => {
    let _version = _themeVersion + 1;
    if (!ModalThemes.has(_version)) {
      _version = 0;
    }
    setThemeVersion(_version);
  }, [_themeVersion]);

  useEffect(() => {
    if (open) {
      document.addEventListener(mobile ? Event.touchend : Event.mouseup, handleDragging.end);
    }

    return () => {
      document.removeEventListener(mobile ? Event.touchend : Event.mouseup, handleDragging.end);
      document.removeEventListener(mobile ? Event.touchmove : Event.mousemove, handleDragging.move);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ThemePaletteComponent = new Map([
    [true, (
      <Tooltip title={`Version ${theme.title}`} placement='top' arrow>
        <IconButton size='small' className={`${theme.paletteIcon}`} onClick={handlePaletteClick}>
          <PaletteRoundedIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    )],
    [false, <Fragment />]
  ]);

  const CloseComponent = new Map([
    [true, (
      <Tooltip title='Close' placement='top' arrow>
        <IconButton size='small' className={`${theme.paletteIcon}`} onClick={handleClose}>
          <CloseRoundedIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    )],
    [false, <Fragment />]
  ]);

  return (
    createPortal(
      <Fade in={open} timeout={FadeInAndOutDuration}>
        <div className={`${classes.modal} ${theme.wrapper}`} style={style} ref={ref.modal}>
            <div className='flex flex-column flex-fill scroll-y-auto'>
              <div className={`modal-heading ${theme.heading}`} ref={ref.heading} onTouchStart={handleDragging.start} onMouseDown={handleDragging.start}>
                <div className='flex flex-fill m-5 align-items-center justify-content-center'>
                  <p className='font-20'>{heading}</p>

                  <div className='absolute right-0 flex gap-8'>
                    {ThemePaletteComponent.get(hasThemePalette)}
                    {CloseComponent.get(hasClose)}
                  </div>
                </div>
              </div>
              <div className="modal-content">
                {content}
              </div>

              <Popup open={saveConfirmation} title={saveConfirmationHeading} type={POPUP_TYPE.INFO}>
                <div className='flex flex-column'>
                  <div className='flex align-items-center m-b-5 font-roboto'>{saveConfirmationMessage}</div>
                  <div className='flex align-items-center justify-content-center'>
                    <PPButton
                      label='Confirm'
                      variant='contained'
                      endGutter
                      onClick={handleSaveConfirmationYes}
                    />
                    <PPButton
                      label='Cancel'
                      variant='cancel'
                      onClick={handleSaveConfirmationNo}
                    />
                  </div>
                </div>
              </Popup>

              <Popup open={closeConfirmation} title={closeConfirmationHeading} type={POPUP_TYPE.INFO}>
                <div className='flex flex-column'>
                  <div className='flex align-items-center m-b-5 font-roboto'>{closeConfirmationMessage}</div>
                  <div className='flex align-items-center justify-content-center'>
                    <PPButton
                      label='Yes, cancel'
                      variant='contained'
                      endGutter
                      onClick={handleCloseConfirmationYes}
                    />
                    <PPButton
                      label='No, do not'
                      variant='cancel'
                      onClick={handleCloseConfirmationNo}
                    />
                  </div>
                </div>
              </Popup>

              <Popup open={responsePopup} title={responsePopupHeading} type={responsePopupType}>
                <div className='flex flex-column'>
                  <div className='flex align-items-center m-b-5 font-roboto'>{responsePopupMessage}</div>
                  <div className='flex align-items-center justify-content-center'>
                    <PPButton
                      label='OK'
                      variant='contained'
                      endGutter
                      onClick={handleResponsePopupOk}
                    />
                  </div>
                </div>
              </Popup>
            </div>
        </div>
      </Fade>,
      root
    )
  );
};

export const CommonModal = withStyles(CommonModalStyle)(Component);

export default CommonModal;
